/**
 * Product Group Helper Functions
 *
 * These utilities handle the formatting and parsing of product group strings
 * that combine productGroupId and name in the format "id:name"
 */

const SEPARATOR = ':'

export interface ProductGroup {
  productGroupId: string
  name: string
}

/**
 * Formats a product group object into a string "id:name"
 * @param productGroupId - The product group ID
 * @param name - The product group name
 * @returns Formatted string "id:name"
 */
export const formatProductGroup = (
  productGroupId: string,
  name: string
): string => {
  return `${productGroupId}${SEPARATOR}${name}`
}

/**
 * Formats a product group object into a string "id:name"
 * @param productGroup - Object with productGroupId and name
 * @returns Formatted string "id:name"
 */
export const formatProductGroupFromObject = (
  productGroup: ProductGroup
): string => {
  return formatProductGroup(productGroup.productGroupId, productGroup.name)
}

/**
 * Parses a product group string "id:name" into its components
 * @param productGroupString - String in format "id:name"
 * @returns Object with productGroupId and name, or null if invalid
 */
export const parseProductGroup = (
  productGroupString: string | null | undefined
): ProductGroup | null => {
  if (!productGroupString) {
    return null
  }

  const firstColonIndex = productGroupString.indexOf(SEPARATOR)
  if (firstColonIndex === -1) {
    return null
  }

  return {
    productGroupId: productGroupString.substring(0, firstColonIndex),
    name: productGroupString.substring(firstColonIndex + 1)
  }
}

/**
 * Extracts just the product group ID from a formatted string
 * @param productGroupString - String in format "id:name"
 * @returns The product group ID, or null if invalid
 */
export const getProductGroupId = (
  productGroupString: string | null | undefined
): string | null => {
  const parsed = parseProductGroup(productGroupString)
  return parsed?.productGroupId ?? null
}

/**
 * Extracts just the product group name from a formatted string
 * @param productGroupString - String in format "id:name"
 * @returns The product group name, or null if invalid
 */
export const getProductGroupName = (
  productGroupString: string | null | undefined
): string | null => {
  const parsed = parseProductGroup(productGroupString)
  return parsed?.name ?? null
}
